import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'

import { kAgentV2MatterApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { MatterArtifactDraftListResponseSchema, type MatterArtifactDraftListResponse } from '@/matter-artifacts/schemas'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

export async function getMatterArtifactDraftsByMatterId(matterId: string): Promise<MatterArtifactDraftListResponse> {
  const transactionId = nanoid()
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error("Could not retrieve user's authentication token.")

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kAgentV2MatterApiBasePath}${encodeURIComponent(matterId)}/drafts`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Transaction-Id': transactionId,
    },
  })
  const { data, parseError } = await readResponseJson(response)

  if (!response.ok) {
    throw new ApiError(response.status, getErrorDetail(data) || `Failed to fetch Matter artifact drafts: ${response.status}`)
  }

  if (parseError) {
    Sentry.captureException(new Error('Matter artifact drafts response JSON parse failed'), {
      extra: {
        ...getResponseDebugContext(response, url),
        matterId,
        parseError: formatUnknownError(parseError),
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Invalid Matter artifact drafts response JSON')
  }

  const parsed = MatterArtifactDraftListResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(new Error('Matter artifact drafts response schema validation failed'), {
      extra: {
        ...getResponseDebugContext(response, url),
        matterId,
        responseKeys: getTopLevelKeys(data),
        issues: parsed.error.issues,
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Invalid Matter artifact drafts response shape', parsed.error.issues)
  }

  return parsed.data
}

function getErrorDetail(data: unknown): string | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string') return detail
  if (detail === undefined || detail === null) return null
  return JSON.stringify(detail)
}
